// src/composables/useAuthRoles.js
import { ref, computed } from 'vue'
import userUtils from '@/common/userUtils'               // 실제 경로 유지
import { logDebug, logWarn, logError } from '@/utils/logHelper' // logger 유틸
import { useRouter } from "vue-router";

export function useAuthRoles() {
  const router = useRouter();

  // userInfo 초기 로딩(안전 가드)
  const userInfo = ref(safeGetUserInfo())
  // 예: { userSuid: "...", userEmail: "...", roles: ["ROLE_USER", ...] }

  // roles 계산(안전 가드)
  const roles = computed(() => {
    const list = userInfo.value?.roles
    return Array.isArray(list) ? list : []
  })

  // 로그인 여부: userSuid 존재 여부로 판정
  const isLoggedIn = computed(() => {
    const suid = userInfo.value?.userSuid
    return typeof suid === 'string' && suid.trim().length > 0
  })

  // 편의: userSuid computed
  const userSuid = computed(() => (isLoggedIn.value ? userInfo.value.userSuid : null))

  /**
   * 로그인 체크:
   * - 미로그인: 모달(스로틀) 오픈 + null 반환
   * - 로그인: userSuid 반환
   */
  function ensureUserLoggedIn(opts = { openModal: true }) {
  // ★ Lazy rehydrate
  try {
    const latest = safeGetUserInfo();
    const cur = userInfo.value;
    const curSuid = cur?.userSuid ?? null;
    const latestSuid = latest?.userSuid ?? null;

    if (
      latestSuid !== curSuid ||
      JSON.stringify(cur?.roles || []) !== JSON.stringify(latest?.roles || [])
    ) {
      userInfo.value = latest;
      logDebug?.("[auth] rehydrated userInfo", maskForLog(userInfo.value));
    }
  } catch (e) {
    logError?.("[auth] ensureUserLoggedIn error", e);
  }

  if (!isLoggedIn.value) {
    if (opts?.openModal !== false) {
      const cur = router.currentRoute.value;
      const fallback = "/";
      const nextPath =
        cur.path === "/login" ? (cur.query.next || fallback) : cur.fullPath;

      router.push({ path: "/login", query: { next: nextPath } });
      logWarn?.("[auth] not logged in → redirect to /login");
    } else {
      logDebug?.("[auth] not logged in (silent)");
    }
    return null;
  }

  return userInfo.value.userSuid;
}


  /** 특정 역할 보유 여부 */
  function hasRole(role) {
    if (!role || typeof role !== 'string') return false
    return roles.value.includes(role)
  }

  /** 주어진 역할 배열 중 하나라도 보유 */
  function hasAnyRole(checkRoles) {
    if (!Array.isArray(checkRoles) || checkRoles.length === 0) return false
    return checkRoles.some(r => typeof r === 'string' && roles.value.includes(r))
  }

  // 단축 메서드
  function isAdmin() { return hasRole('ROLE_ADMIN') }
  function isUser() { return hasRole('ROLE_USER') }
  function isVendor() { return hasRole('ROLE_VENDOR') }

  /**
   * 게스트 정의:
   * - userSuid 없음 또는 roles 비어있음
   */
  function isGuest() {
    if (!isLoggedIn.value) return true
    return roles.value.length === 0
  }

  /**
   * userInfo 재로딩(선택적으로 next 주입 가능)
   */
  function reloadUserInfo(next) {
    try {
      userInfo.value = next ?? safeGetUserInfo()
      logDebug?.('[auth] userInfo reloaded', maskForLog(userInfo.value))
    } catch (e) {
      logError?.('[auth] reloadUserInfo error', e)
      userInfo.value = null
    }
  }

  /**
   * 외부에서 명시적으로 세팅하고 싶을 때 사용
   */
  function setUserInfo(next) {
    userInfo.value = next ?? null
    logDebug?.('[auth] userInfo set', maskForLog(userInfo.value))
  }

  /**
   * 역할 요구 가드(편의):
   * - 로그인 필요 시 ensureUserLoggedIn 호출
   * - requiredRoles가 비었으면 로그인만 확인
   */
  function requireRoles(requiredRoles = [], { openModalIfNoLogin = true } = {}) {
    const suid = ensureUserLoggedIn({ openModal: openModalIfNoLogin })
    if (!suid) return false
    if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) return true
    return hasAnyRole(requiredRoles)
  }

  return {
    // userInfo 관련
    userInfo,
    userSuid,
    reloadUserInfo,
    setUserInfo,

    // 로그인/역할 관련
    isLoggedIn,
    isGuest,
    ensureUserLoggedIn,
    roles,
    hasRole,
    hasAnyRole,
    isAdmin,
    isUser,
    isVendor,

    // 편의 가드
    requireRoles,
  }
}

// ---------- 내부 유틸 ----------
function safeGetUserInfo() {
  try {
    const ui = userUtils.getUserInfo()
    if (ui && typeof ui === 'object') return ui
    return null
  } catch (e) {
    logWarn?.('[auth] getUserInfo threw', e)
    return null
  }
}

function maskForLog(ui) {
  if (!ui) return ui
  const { userEmail, userSuid, ...rest } = ui
  return {
    userEmail: userEmail ? '***' : null,
    userSuid: userSuid ? '***' : null,
    ...rest,
  }
}
